/**
 * Wang sebagai integer sen.
 *
 * Peraturan (ADR-0005):
 * - Semua amaun wang ialah integer sen. Tiada `float`, tiada `numeric` untuk wang.
 * - Pembundaran ialah *half-up* (0.5 → 1, menjauhi sifar), bukan banker's rounding.
 * - Penukaran daripada teks pengguna berlaku **sekali sahaja**, di sempadan sistem.
 */
export const SEN_PER_RINGGIT = 100;
/** Had atas ringgit yang masih selamat dalam `number` (sen ≤ 2^53−1). */
const MAX_WHOLE_RINGGIT = 9_000_000_000_000;
/** Baling jika nilai bukan integer dalam had selamat. */
export function assertSen(value, label = 'amaun') {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        throw new TypeError(`${label} mesti nombor terhingga, diterima ${String(value)}`);
    }
    if (!Number.isInteger(value)) {
        throw new RangeError(`${label} mesti integer sen, diterima ${value}`);
    }
    if (!Number.isSafeInteger(value)) {
        throw new RangeError(`${label} melebihi had selamat integer: ${value}`);
    }
}
/**
 * Bahagi integer dengan pembundaran half-up (menjauhi sifar).
 *
 * Ini satu-satunya tempat pembundaran berlaku dalam sistem. `Math.round` tidak
 * digunakan kerana ia membundar −0.5 kepada −0 (ke arah sifar), yang menghasilkan
 * ketidakadilan pada diskaun dan pembetulan negatif.
 *
 * @param numerator integer (boleh negatif)
 * @param denominator integer positif
 */
export function divRoundHalfUp(numerator, denominator) {
    if (!Number.isSafeInteger(numerator)) {
        throw new RangeError(`pengangka mesti integer selamat, diterima ${numerator}`);
    }
    if (!Number.isSafeInteger(denominator) || denominator <= 0) {
        throw new RangeError(`penyebut mesti integer positif, diterima ${denominator}`);
    }
    const magnitude = Math.abs(numerator);
    const quotient = Math.floor(magnitude / denominator);
    const remainder = magnitude - quotient * denominator;
    const rounded = remainder * 2 >= denominator ? quotient + 1 : quotient;
    const result = numerator < 0 ? -rounded : rounded;
    if (!Number.isSafeInteger(result)) {
        throw new RangeError(`hasil pembundaran melebihi had selamat: ${result}`);
    }
    return result;
}
/**
 * Hurai teks amaun pengguna kepada sen.
 *
 * Diterima: `"45"`, `"45.5"`, `"45.50"`, `"RM 45.50"`, `"1,234.56"`, `"-12.00"`, `".5"`.
 * Ditolak: lebih daripada 2 tempat perpuluhan, aksara lain, kosong.
 */
export function parseMYR(input) {
    if (typeof input !== 'string') {
        throw new TypeError('amaun mesti teks');
    }
    const cleaned = input
        .trim()
        .replace(/^rm\s*/i, '')
        .replace(/\s+/g, '');
    if (cleaned === '') {
        throw new RangeError('amaun kosong');
    }
    let normalised = cleaned;
    if (/^-?\d{1,3}(,\d{3})+(\.\d{0,2})?$/.test(normalised)) {
        normalised = normalised.replace(/,/g, '');
    }
    const match = /^(-)?(\d*)(?:\.(\d{0,2}))?$/.exec(normalised);
    const sign = match?.[1] === '-' ? -1 : 1;
    const wholeRaw = match?.[2] ?? '';
    const fracRaw = match?.[3] ?? '';
    if (match === null || (wholeRaw === '' && fracRaw === '')) {
        throw new RangeError(`bukan amaun MYR yang sah: "${input}"`);
    }
    const whole = wholeRaw === '' ? 0 : Number(wholeRaw);
    if (!Number.isSafeInteger(whole) || whole > MAX_WHOLE_RINGGIT) {
        throw new RangeError(`amaun terlalu besar: "${input}"`);
    }
    const frac = fracRaw === '' ? 0 : Number(fracRaw.padEnd(2, '0'));
    const sen = whole * SEN_PER_RINGGIT + frac;
    assertSen(sen, 'amaun');
    return sign * sen;
}
/** Format sen kepada teks MYR tanpa pemisah ribu: `4500` → `"45.00"`. */
export function formatMYR(sen) {
    assertSen(sen);
    const negative = sen < 0;
    const magnitude = Math.abs(sen);
    const whole = Math.floor(magnitude / SEN_PER_RINGGIT);
    const frac = magnitude - whole * SEN_PER_RINGGIT;
    return `${negative ? '-' : ''}${whole}.${String(frac).padStart(2, '0')}`;
}
/** Format sen dengan pemisah ribu: `123456` → `"1,234.56"`. */
export function formatMYRGrouped(sen) {
    const plain = formatMYR(sen);
    const negative = plain.startsWith('-');
    const body = negative ? plain.slice(1) : plain;
    const [whole = '0', frac = '00'] = body.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `${negative ? '-' : ''}${grouped}.${frac}`;
}
/** Jumlah sen dengan semakan limpahan. Guna ini, bukan `reduce` mentah. */
export function sumSen(values) {
    let total = 0;
    for (const value of values) {
        assertSen(value, 'amaun');
        total += value;
        if (!Number.isSafeInteger(total)) {
            throw new RangeError(`jumlah amaun melebihi had selamat: ${total}`);
        }
    }
    return total;
}
/** Bundarkan kepada gandaan 5 sen terdekat (pilihan per kedai). */
export function roundToNearest5Sen(sen) {
    assertSen(sen);
    const magnitude = Math.abs(sen);
    const quotient = Math.floor(magnitude / 5);
    const remainder = magnitude - quotient * 5;
    // remainder ∈ {0,1,2,3,4}. Titik separuh (2.5) tidak mungkin kerana sen integer.
    const rounded = remainder >= 3 ? (quotient + 1) * 5 : quotient * 5;
    const result = sen < 0 ? -rounded : rounded;
    assertSen(result, 'amaun dibundar');
    return result;
}
//# sourceMappingURL=money.js.map